import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, MessageCircleHeart } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useTenantModuleEnabled } from "@/entities/dashboard";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import {
  DEFAULT_KEFU_WELCOME,
  KEFU_WELCOME_MAX,
  KEFU_WELCOME_QUERY_KEY,
  kefuWelcomeService,
} from "../api/kefuWelcomeService";

/**
 * 企微页签「欢迎语」设置(1010 一期补完契约 5,当初方案「两边共用一份配置」的企微侧)。
 * 客户进入企微客服会话时收到;网页 / H5 接入点欢迎语留空时也回落到这一句。
 * 读 = 租户成员;写 = TENANT_MANAGER+(真正的闸在 api)。没开通客服舱不请求(接口在 ai_kefu 门内)。
 */
export function WeComWelcomeCard({ className }: { className?: string }) {
  const { t } = useTranslation("platform");
  const k = (s: string, o?: Record<string, unknown>) => t(`channelMgmt.welcome.${s}`, o);
  const { effectiveRole } = useAuth();
  const canWrite = isTenantManagerPlus(effectiveRole);
  const kefuEnabled = useTenantModuleEnabled("ai_kefu");

  const q = useQuery({
    queryKey: KEFU_WELCOME_QUERY_KEY,
    queryFn: kefuWelcomeService.get,
    enabled: kefuEnabled === true,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (q.data) setDraft(q.data.text ?? "");
  }, [q.data]);

  const save = useMutationWithToast({
    mutationFn: (text: string) => kefuWelcomeService.put(text),
    successToast: k("saved"),
    invalidateKeys: [KEFU_WELCOME_QUERY_KEY],
  });

  if (kefuEnabled !== true) return null;
  const tooLong = draft.trim().length > KEFU_WELCOME_MAX;
  const dirty = (q.data?.text ?? "") !== draft.trim();

  return (
    <Card className={className} data-testid="wecom-welcome-card">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <MessageCircleHeart className="h-4 w-4 text-primary" />
          {k("title")}
        </div>
        <p className="text-xs text-muted-foreground">{k("desc")}</p>
        <Label htmlFor="kefu-welcome" className="sr-only">
          {k("title")}
        </Label>
        <Textarea
          id="kefu-welcome"
          rows={3}
          value={draft}
          maxLength={KEFU_WELCOME_MAX}
          disabled={!canWrite || q.isLoading || q.isError}
          placeholder={k("placeholder", { text: DEFAULT_KEFU_WELCOME })}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs text-muted-foreground">
            {q.isError ? k("loadFailed") : k("hint", { max: KEFU_WELCOME_MAX })}
          </span>
          {canWrite && (
            <Button
              size="sm"
              disabled={!dirty || tooLong || save.isPending || q.isError}
              onClick={() => save.mutate(draft.trim())}
              data-testid="wecom-welcome-save"
            >
              {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {k("save")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
